/**
 * 3D Bomb entity — procedural model using Three.js geometry.
 * Planted state: bomb on ground with blinking red light.
 * Carried state: hidden from scene (HUD icon only).
 */

import * as THREE from 'three';
import { markEffect } from '../renderer/effect-loop';

const BOMB_COLOR = 0x2a2a2a;
const ACCENT_RED = 0xe94560;
const LIGHT_ON = 0xff2222;
const LIGHT_OFF = 0x440000;

// F-GP-010 (audits/2026-05-07): `BOMB_SOUND_IDS` constant removed —
// audio is played via direct string keys (`audio/event-sounds.ts`,
// `audioManager.play('bomb_plant')`), so the constant added zero coupling.

export class BombEntity {
  readonly group: THREE.Group;

  private body: THREE.Mesh;
  private top: THREE.Mesh;
  private light: THREE.Mesh;
  private lightMat: THREE.MeshStandardMaterial;

  private planted = false;
  private carried = false;
  private _disposed = false;
  private blinkTimer = 0;
  private blinkOn = true;
  private blinkRate = 1.0; // blinks per second (increases with urgency)
  private totalTime = 40;
  private timeRemaining = 40;

  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'bomb';

    // Body — box
    const bodyGeo = new THREE.BoxGeometry(0.4, 0.2, 0.25);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: BOMB_COLOR,
      roughness: 0.7,
      metalness: 0.3,
    });
    this.body = new THREE.Mesh(bodyGeo, bodyMat);
    this.body.castShadow = true;
    this.group.add(this.body);

    // Top cylinder (detonator)
    const topGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.12, 8);
    const topMat = new THREE.MeshStandardMaterial({
      color: 0x555555,
      roughness: 0.5,
      metalness: 0.5,
    });
    this.top = new THREE.Mesh(topGeo, topMat);
    this.top.position.set(0.12, 0.16, 0);
    this.top.castShadow = true;
    this.group.add(this.top);

    // Blinking light (small sphere on top)
    const lightGeo = new THREE.SphereGeometry(0.03, 8, 8);
    this.lightMat = new THREE.MeshStandardMaterial({
      color: LIGHT_OFF,
      emissive: LIGHT_OFF,
      emissiveIntensity: 0.5,
      roughness: 0.3,
      metalness: 0.1,
    });
    this.light = new THREE.Mesh(lightGeo, this.lightMat);
    this.light.position.set(0.12, 0.23, 0);
    this.group.add(this.light);

    // Wire details — simple thin cylinders
    const wireGeo = new THREE.CylinderGeometry(0.008, 0.008, 0.15, 4);
    const wireMat = new THREE.MeshStandardMaterial({ color: ACCENT_RED, roughness: 0.8 });
    const wire1 = new THREE.Mesh(wireGeo, wireMat);
    wire1.position.set(-0.1, 0.1, 0.05);
    wire1.rotation.z = Math.PI * 0.3;
    this.group.add(wire1);

    const wire2 = new THREE.Mesh(wireGeo, wireMat);
    wire2.position.set(-0.1, 0.1, -0.05);
    wire2.rotation.z = -Math.PI * 0.25;
    this.group.add(wire2);

    // Start hidden
    this.group.visible = false;
  }

  /** Place bomb on ground at world position. */
  setPlanted(pos: THREE.Vector3): void {
    this.planted = true;
    this.carried = false;
    this.group.visible = true;
    this.group.position.copy(pos);
    // Sit on ground
    this.group.position.y = pos.y + 0.1;
    this.blinkTimer = 0;
    this.blinkOn = true;
    this.blinkRate = 1.0;
  }

  /** Mark as carried (hide 3D model — HUD handles carrier indicator). */
  setCarried(): void {
    this.planted = false;
    this.carried = true;
    this.group.visible = false;
  }

  /** Set bomb timer state for blink rate calculation. */
  setTimerState(timeRemaining: number, totalTime: number): void {
    this.timeRemaining = timeRemaining;
    this.totalTime = totalTime;

    // Blink rate increases as timer runs down
    const ratio = totalTime > 0 ? timeRemaining / totalTime : 1;
    if (ratio < 0.15) {
      this.blinkRate = 8.0;
    } else if (ratio < 0.3) {
      this.blinkRate = 4.0;
    } else if (ratio < 0.5) {
      this.blinkRate = 2.0;
    } else {
      this.blinkRate = 1.0;
    }
  }

  /** Whether the bomb is currently planted. */
  isPlantedState(): boolean {
    return this.planted;
  }

  /** Get the current bomb timer remaining (seconds). */
  getTimeRemaining(): number {
    return this.timeRemaining;
  }

  /** Called every frame with delta time in seconds. */
  update(dt: number): void {
    if (!this.planted || this._disposed) return;

    // Blink the light
    this.blinkTimer += dt;
    const interval = 1 / this.blinkRate;
    if (this.blinkTimer >= interval) {
      this.blinkTimer -= interval;
      this.blinkOn = !this.blinkOn;
      this.lightMat.color.setHex(this.blinkOn ? LIGHT_ON : LIGHT_OFF);
      this.lightMat.emissive.setHex(this.blinkOn ? LIGHT_ON : LIGHT_OFF);
      this.lightMat.emissiveIntensity = this.blinkOn ? 2.0 : 0.3;
    }
  }

  /** Remove from scene and dispose geometry/materials. */
  dispose(): void {
    this._disposed = true;
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
    this.group.removeFromParent();
  }
}

// ---------------------------------------------------------------------------
// Bomb site ground markers — simple "A" / "B" labels on the ground
// Coordinates must match server/internal/game/bomb.go BombSitesByMap
// ---------------------------------------------------------------------------

interface BombSiteAABB {
  name: string;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/**
 * Per-map bomb site AABBs. Kept in sync with server/internal/game/bomb.go
 * BombSitesByMap.
 */
const BOMB_SITES_BY_MAP: Record<string, BombSiteAABB[]> = {
  de_outpost_v6: [
    { name: 'A', minX: -65, maxX: -32, minZ: -50, maxZ: -22 },
    { name: 'B', minX: 35, maxX: 64, minZ: -26, maxZ: 0 },
  ],
};

export function getBombSitesForMap(mapId?: string): BombSiteAABB[] {
  if (mapId && BOMB_SITES_BY_MAP[mapId]) return BOMB_SITES_BY_MAP[mapId];
  if (mapId) {
    console.warn(`[bomb] No bomb sites configured for map "${mapId}" — markers will not render.`);
  }
  return [];
}

function createSiteMarker(label: string, cx: number, cz: number): THREE.Group {
  const group = new THREE.Group();
  group.name = `bombsite_${label}`;

  // Ground ring
  const ringGeo = new THREE.RingGeometry(2.0, 2.4, 32);
  const ringMat = new THREE.MeshBasicMaterial({
    color: ACCENT_RED,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.set(cx, 0.05, cz);
  markEffect(ring); // exclude from bullet raycasts
  group.add(ring);

  // Letter label — canvas texture
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const c = canvas.getContext('2d')!;
  c.clearRect(0, 0, 128, 128);
  c.fillStyle = '#e94560';
  c.font = 'bold 96px monospace';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillText(label, 64, 64);

  const tex = new THREE.CanvasTexture(canvas);
  // Release canvas backing-store — texture is already uploaded to GPU
  (tex.source as { data: HTMLCanvasElement | null }).data = null;
  const planeMat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const planeGeo = new THREE.PlaneGeometry(2.5, 2.5);
  const plane = new THREE.Mesh(planeGeo, planeMat);
  plane.rotation.x = -Math.PI / 2;
  plane.position.set(cx, 0.06, cz);
  markEffect(plane); // exclude from bullet raycasts
  group.add(plane);

  return group;
}

/** Create bomb site markers for the given map and add to scene. Returns dispose fn. */
export function createBombSiteMarkers(scene: THREE.Scene, mapId?: string): () => void {
  const sites = getBombSitesForMap(mapId);
  const markers: THREE.Group[] = [];

  for (const s of sites) {
    const cx = (s.minX + s.maxX) / 2;
    const cz = (s.minZ + s.maxZ) / 2;
    const marker = createSiteMarker(s.name, cx, cz);
    scene.add(marker);
    markers.push(marker);
  }

  return () => {
    for (const marker of markers) {
      marker.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            (child.material as THREE.MeshBasicMaterial).map?.dispose();
            child.material.dispose();
          }
        }
      });
      marker.removeFromParent();
    }
  };
}
